import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { City } from '@/data/cities';
import { worldCountries } from '@/data/countries/index';

interface UseCityDataOptions {
  fallbackToStatic?: boolean;
}

/**
 * Unified hook to fetch city data from Supabase with intelligent fallback
 * Primary source: Supabase cities table (32,853 cities)
 * Fallback: worldCountries.topCities (static data)
 */
export const useCityData = (
  citySlug: string | undefined,
  options: UseCityDataOptions = { fallbackToStatic: true }
) => {
  return useQuery({
    queryKey: ['city-unified', citySlug],
    queryFn: async () => {
      if (!citySlug) return null;

      // 1. Try to fetch from Supabase (primary source) with timeout to avoid hanging spinners
      try {
        const timeoutMs = 1500;
        const dbRequest = supabase
          .from('cities')
          .select('*')
          .eq('slug', citySlug)
          .eq('is_active', true)
          .maybeSingle();

        const timeoutPromise = new Promise<"TIMEOUT">((resolve) => {
          setTimeout(() => resolve('TIMEOUT'), timeoutMs);
        });

        const result: any = await Promise.race([dbRequest, timeoutPromise]);

        if (result !== 'TIMEOUT') {
          const { data, error } = result as { data: any; error: any };

          if (error) {
            console.warn('Error fetching city from database:', error);
          }

          if (data) {
            // Validate required fields
            if (!data.name || !data.slug) {
              console.error('Invalid city data - missing required fields:', data);
              return null;
            }

            // Transform to City type with safe defaults
            const city: City = {
              id: data.id || `city-${data.slug}`,
              name: (data.name || '').trim(),
              country: (data.country || 'Unknown Country').trim(),
              countryCode: determineCountryCode(data.country || ''),
              population: 100000,
              coordinates: {
                lat: data.latitude || 0,
                lng: data.longitude || 0
              },
              language: 'en',
              timezone: 'UTC',
              slug: (data.slug || citySlug).trim(),
              state_province: (data.state_province || '').trim() || undefined
            };
            return city;
          }
        } else {
          console.warn(`Supabase cities query timed out after ${timeoutMs}ms for slug: ${citySlug}`);
        }
      } catch (e) {
        console.warn('Unexpected error fetching city from database:', e);
      }

      // 2. Fallback to worldCountries if enabled
      if (options.fallbackToStatic) {
        for (const country of worldCountries) {
          const foundCity = country.topCities.find(c => c.slug === citySlug);
          if (foundCity) {
            const city: City = {
              id: `${foundCity.slug}-${country.code.toLowerCase()}`,
              name: foundCity.name,
              country: country.name,
              countryCode: country.code,
              population: foundCity.population,
              coordinates: { lat: 0, lng: 0 },
              language: 'en',
              timezone: 'UTC',
              slug: foundCity.slug
            };
            return city;
          }
        }
      }

      return null;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on data validation errors
      if (error?.message?.includes('Invalid city data')) return false;
      // Retry network/database errors up to 2 times
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchOnWindowFocus: false,
    enabled: !!citySlug
  });
};

// Helper to determine country code from country name with intelligent fallback
function determineCountryCode(countryName: string): string {
  if (!countryName) return 'XX';

  const normalized = countryName.toLowerCase().trim();
  
  // 1. Try exact match
  const exactMatch = worldCountries.find(c => 
    c.name.toLowerCase() === normalized
  );
  if (exactMatch) return exactMatch.code;

  // 2. Try partial match
  const partialMatch = worldCountries.find(c =>
    c.name.toLowerCase().includes(normalized) ||
    normalized.includes(c.name.toLowerCase())
  );
  if (partialMatch) return partialMatch.code;

  // 3. Common country name mappings
  const countryMappings: Record<string, string> = {
    'iran': 'IR',
    'islamic republic of iran': 'IR',
    'united states': 'US',
    'usa': 'US',
    'united states of america': 'US',
    'uk': 'GB',
    'united kingdom': 'GB',
    'england': 'GB',
    'britain': 'GB',
    'great britain': 'GB',
    'russia': 'RU',
    'russian federation': 'RU',
    'south korea': 'KR',
    'korea': 'KR',
    'north korea': 'KP',
    'vietnam': 'VN',
    'viet nam': 'VN',
    'netherlands': 'NL',
    'holland': 'NL',
    'czechia': 'CZ',
    'czech republic': 'CZ',
    'turkey': 'TR',
    'türkiye': 'TR',
  };

  const mapped = countryMappings[normalized];
  if (mapped) return mapped;

  // 4. Default fallback
  console.warn(`Could not determine country code for: ${countryName}`);
  return 'XX';
}
